<template>
  <div class="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 lg:max-w-7xl lg:px-8">
    <div v-if="!finished">
      <div v-if="isAnnualReportOrder"
           class="mb-6 rounded-lg border-2 border-red-600 bg-red-50 p-4 text-center">
        <p class="text-lg font-extrabold uppercase tracking-wide text-red-700">
          &#9888; Annual Reports Were Due By May 1
        </p>
        <p class="mt-1 text-sm font-medium text-red-800">
          Businesses which file late must pay an additional $400 in late fees.
          <span class="font-bold">ACT NOW</span> to avoid administrative dissolution of your company.
        </p>
      </div>

      <form @submit.prevent="submit" class="lg:grid lg:grid-cols-5 lg:gap-x-4 xl:gap-x-4">
        <!-- Order summary -->
        <div class="col-span-3 mt-0 pt-0">
          <h2 class="text-lg font-medium text-gray-900">Mailing & Billing Information</h2>
          <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
            <div>
              <label for="first-name" class="block text-sm font-medium text-gray-700">First name</label>
              <div class="mt-1">
                <input @blur="removeErrorsOnUpdate"
                       :class="{'border-2 border-red-500': form.errors.first_name}" v-model="form.first_name"
                       type="text" id="first-name" name="first-name" autocomplete="given-name"
                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.first_name" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.first_name" :key="index">
                      {{ error }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <label for="last-name" class="block text-sm font-medium text-gray-700">Last name</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :class="{'border-2 border-red-500': form.errors.last_name}" v-model="form.last_name"
                    type="text" id="last-name" name="last-name" autocomplete="family-name"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.last_name" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.last_name" :key="index">
                      {{ error }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>


            <div class="sm:col-span-2">
              <label for="phone" class="block text-sm font-medium text-gray-700">Phone</label>
              <div class="mt-1">
                <input @blur="removeErrorsOnUpdate"
                       :class="{'border-2 border-red-500': form.errors.phone}" v-model="form.phone" type="text"
                       name="phone" id="phone" autocomplete="tel"
                       class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.phone" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.phone" :key="index">{{
                        error
                      }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="sm:col-span-2">
              <label for="email-address1" class="block text-sm font-medium text-gray-700">Email address</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :class="{'border-2 border-red-500': form.errors.email}" v-model="form.email" type="email"
                    id="email-address1" name="email-address" autocomplete="email"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.email" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.email" :key="index">{{
                        error
                      }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="sm:col-span-2">
              <label for="company" class="block text-sm font-medium text-gray-700">Company</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :readonly="document"
                    v-model="form.company_name" type="text" name="company" id="company"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
              </div>
            </div>

            <div class="sm:col-span-2">
              <label for="address" class="block text-sm font-medium text-gray-700">Address</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :class="{'border-2 border-red-500': form.errors.address}" v-model="form.address" type="text"
                    name="address" id="address" autocomplete="street-address"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.address" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.address" :key="index">
                      {{ error }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>


            <div>
              <label for="city" class="block text-sm font-medium text-gray-700">City</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :class="{'border-2 border-red-500': form.errors.city}" v-model="form.city" type="text"
                    name="city" id="city" autocomplete="address-level2"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.city" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.city" :key="index">{{
                        error
                      }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>


            <div>
              <label for="region" class="block text-sm font-medium text-gray-700">State / Province</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :class="{'border-2 border-red-500': form.errors.state}" v-model="form.state" type="text"
                    name="region" id="region" autocomplete="address-level1"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.state" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.state" :key="index">{{
                        error
                      }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <label for="postal-code" class="block text-sm font-medium text-gray-700">Postal code</label>
              <div class="mt-1">
                <input
                    @blur="removeErrorsOnUpdate"
                    :class="{'border-2 border-red-500': form.errors.zip}" v-model="form.zip" type="text"
                    name="postal-code" id="postal-code" autocomplete="postal-code"
                    class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"/>
                <div v-if="form.errors.zip" class="m-2">
                  <ul>
                    <li class="text-red-600 text-sm" v-for="(error, index) in form.errors.zip" :key="index">{{
                        error
                      }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <FormEinSection
              v-if="addEinSection"
              ref="einSectionRef"
              :formData="form"
              :hasPrimaryEin="hasPrimaryEin"
              :brandUuid="props.brandInfoProp?.uuid"
              @remove="removeEin()"
              @updateFormData="updateEINForm"
          ></FormEinSection>
          <FormBoirSection
              v-if="addBoirSection"
              :formData="form"
              :hasPrimaryBoir="hasPrimaryBoir"
              :addEinSectionProp="form.add_ein_section"
              @remove="removeBoir()"
              @addEinToSelectedItems="addEinToSelectedItems()"
              @updateFormData="updateBOIRForm"

          ></FormBoirSection>

        </div>

        <!-- Order summary -->
        <div class="col-span-2 mt-10 lg:mt-0">
          <!-- Clean card: hairline border, white background, no ornaments -->
          <div class="rounded-lg border border-slate-200 bg-white shadow-sm">

            <!-- Inline header — serif, modest -->
            <div class="px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 class="font-serif text-xl text-slate-900">Order Summary</h2>
            </div>

            <!-- Line items -->
            <ul role="list" class="divide-y divide-slate-100">
              <h3 class="sr-only">Items in your cart</h3>
              <template v-for="(product,index) in selectedItems" :key="index">
                <li v-if="!(showLlpCallout && isLaborLawPosterCode(product.code))"
                    class="px-6 py-4">
                  <div class="flex gap-4">
                    <img :src="'/images/products/'+product.image" :alt="product.imageAlt"
                         class="h-14 w-14 flex-shrink-0 rounded-md object-cover ring-1 ring-slate-200"/>
                    <div class="flex min-w-0 flex-1 flex-col">
                      <div class="flex items-start justify-between gap-2">
                        <div class="min-w-0 flex-1">
                          <h4 class="text-sm font-semibold text-slate-900">
                            <a :href="product.href" class="hover:underline">{{ product.title }}</a>
                          </h4>
                          <p v-if="product.subTitle" class="mt-0.5 text-xs text-slate-500">{{ product.subTitle }}</p>
                          <p v-if="product.description" class="mt-1 text-xs leading-relaxed text-slate-500">{{ product.description }}</p>
                        </div>
                        <button v-if="!product.isPrimary" type="button"
                                @click="deleteItem(product.code)"
                                class="flex h-6 w-6 flex-shrink-0 items-center justify-center text-slate-400 transition-colors hover:text-red-600"
                                aria-label="Remove item">
                          <TrashIcon class="h-4 w-4" aria-hidden="true"/>
                        </button>
                      </div>

                      <!-- Price line, right-aligned -->
                      <div class="mt-2 flex items-baseline justify-between gap-2">
                        <div class="flex items-center gap-1.5">
                          <span v-if="promoApplies(product)"
                                class="text-[10px] font-semibold uppercase tracking-wider text-red-600">
                            {{ promotionProp.discount_percent_label }}
                          </span>
                          <span v-if="promoApplies(product)" class="text-xs text-slate-400 line-through">
                            ${{ Number(product.price).toFixed(2) }}
                          </span>
                        </div>
                        <span class="font-serif text-base font-semibold tabular-nums text-slate-900">
                          ${{ Number(effectivePrice(product)).toFixed(2) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Subscription option — same emerald treatment as the Annual
                       Report enrollment panel below so both $0-today offers read
                       as one design system. -->
                  <div v-if="product.has_subscription_option_product_id"
                       class="mt-3 rounded border-l-2 border-emerald-600 bg-emerald-50/60 px-3 py-2.5">
                    <label :for="'certificate-subscription-' + index" class="flex cursor-pointer items-start gap-2.5">
                      <input
                          :id="'certificate-subscription-' + index"
                          type="checkbox"
                          v-model="product.add_subscription"
                          class="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-emerald-600 text-emerald-700 focus:ring-emerald-600"
                      />
                      <span class="flex-1 text-xs">
                        <span class="flex flex-wrap items-baseline gap-1.5">
                          <span class="font-semibold text-slate-900">Add Yearly {{product.name}} Subscription</span>
                          <span class="inline-flex items-center rounded-sm bg-emerald-600 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                            No money now
                          </span>
                        </span>
                        <span class="mt-0.5 block leading-relaxed text-slate-600">
                          <span class="font-semibold text-slate-900">Nothing is charged today.</span>
                          Card billed
                          <span class="font-semibold text-slate-900">${{product.subscription_option_product.price}}</span>
                          on January 5 next year, and renews annually each January 5. Cancel anytime.
                        </span>
                      </span>
                      <span class="flex-shrink-0 font-serif text-xs font-semibold text-emerald-700">$0.00 today</span>
                    </label>
                  </div>
                </li>
              </template>
            </ul>

            <!-- Annual Report enrollment — $0-today bundle (annual_report service
                 + state filing fee) deferred to a January 3 yearly subscription.
                 Unchecked by default: enrollment is the visitor's choice. -->
            <div v-if="showAnnualReportEnrollment"
                 class="mx-6 mt-4 mb-2 rounded border-l-2 border-emerald-600 bg-emerald-50/60 px-3 py-2.5">
              <label for="annual-report-enrollment" class="flex cursor-pointer items-start gap-2.5">
                <input
                    id="annual-report-enrollment"
                    type="checkbox"
                    v-model="form.add_annual_report_upsell"
                    class="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-emerald-600 text-emerald-700 focus:ring-emerald-600"
                />
                <span class="flex-1 text-xs">
                  <span class="flex flex-wrap items-baseline gap-1.5">
                    <span class="font-semibold text-slate-900">Enroll in Annual Report Service</span>
                    <span class="inline-flex items-center rounded-sm bg-emerald-600 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                      No money now
                    </span>
                  </span>
                  <span class="mt-0.5 block leading-relaxed text-slate-600">
                    <span class="font-semibold text-slate-900">Nothing is charged today.</span>
                    Each year we prepare and file your {{ jurisdictionDisplayName }} Annual Report
                    so your company stays in good standing. Your first payment of
                    <span class="font-semibold text-slate-900">${{ annualReportEnrollmentTotal }}</span>
                    <template v-if="annualReportEnrollmentStateFee">
                      (${{ Number(annualReportEnrollment.price).toFixed(2) }} service +
                      ${{ Number(annualReportEnrollmentStateFee.price).toFixed(2) }} state fee)
                    </template>
                    is on January 3, and renews annually each January 3. Cancel anytime.
                  </span>
                  <span class="mt-1 block leading-relaxed font-semibold text-red-600">
                    &#9888; Companies that miss their Annual Report are administratively dissolved by the
                    state — enrolling prevents losing your company over a missed payment.
                  </span>
                </span>
                <span class="flex-shrink-0 font-serif text-xs font-semibold text-emerald-700">$0.00 today</span>
              </label>
            </div>

            <!-- Labor Law Poster — quiet inset, no eyebrow pin -->
            <div v-if="laborLawPosterAvailable && showLlpCallout"
                 class="mx-6 mb-2 rounded border-l-2 border-amber-500 bg-amber-50/60 px-3 py-2.5">
              <label class="flex cursor-pointer items-start gap-2.5">
                <input
                    type="checkbox"
                    :checked="isLaborLawPosterInCart"
                    @change="isLaborLawPosterInCart ? removeLaborLawPoster() : addLaborLawPoster()"
                    class="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-amber-600 text-amber-700 focus:ring-amber-600"
                />
                <img v-if="laborLawPosterAvailable.image"
                     :src="'/images/products/' + laborLawPosterAvailable.image"
                     :alt="laborLawPosterAvailable.name"
                     class="h-10 w-10 flex-shrink-0 rounded bg-white object-cover ring-1 ring-amber-200"/>
                <span class="flex-1 text-xs">
                  <span class="flex flex-wrap items-baseline gap-1.5">
                    <span class="font-semibold text-slate-900">
                      <span v-if="isLaborLawPosterInCart">Added: </span><span v-else>Add </span>{{ laborLawPosterAvailable.name }}
                    </span>
                    <span v-if="promoApplies(laborLawPosterAvailable)"
                          class="text-[10px] font-semibold uppercase tracking-wider text-red-600">
                      {{ promotionProp.discount_percent_label }}
                    </span>
                  </span>
                  <span class="mt-0.5 block leading-relaxed text-slate-600">
                    State + federal poster with automatic regulation updates. $20,000 fine reimbursement guarantee.
                  </span>
                </span>
                <span class="flex flex-shrink-0 flex-col items-end gap-0.5 font-serif">
                  <span v-if="promoApplies(laborLawPosterAvailable)"
                        class="text-[10px] text-slate-400 line-through">
                    ${{ Number(laborLawPosterAvailable.price).toFixed(2) }}/yr
                  </span>
                  <span class="text-sm font-semibold tabular-nums text-slate-900">
                    ${{ Number(effectivePrice(laborLawPosterAvailable)).toFixed(2) }}<span class="text-[10px] font-normal text-slate-500">/yr</span>
                  </span>
                </span>
              </label>
            </div>

            <!-- Cross-sells — clearly framed section with its own header + tinted panel -->
            <div v-if="availableItems && availableItems.length" class="mx-6 mt-4 mb-2 rounded-md border border-slate-200 bg-slate-50 p-4">
              <div class="mb-3 flex items-baseline justify-between">
                <h3 class="text-sm font-semibold text-slate-900">Add to your order</h3>
                <span class="text-xs text-slate-500">Recommended</span>
              </div>
              <div class="space-y-2">
                <template v-for="(child,index) in availableItems" :key="index">
                  <div v-if="!isSubscriptionOptionForSelected(child) && !hideLlpFromOtherServices(child)">
                    <button v-if="!hasItem(child.code)"
                            @click="addItems(index)"
                            type="button"
                            class="group flex w-full items-center justify-between gap-3 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-left shadow-sm transition-all hover:border-slate-900 hover:shadow-md">
                      <span class="flex min-w-0 items-center gap-2.5">
                        <!-- Prominent + badge so the affordance reads as a button -->
                        <span class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-white transition-colors group-hover:bg-emerald-700">
                          <svg class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
                          </svg>
                        </span>
                        <span class="flex min-w-0 flex-col">
                          <span class="flex items-baseline gap-1.5">
                            <span v-if="child.code=='ein_registration' || child.code=='boir'"
                                  class="text-[10px] font-bold uppercase tracking-wider text-red-600">Critical</span>
                            <span class="text-sm font-semibold text-slate-900">{{ child.name }}</span>
                          </span>
                        </span>
                      </span>
                      <span class="flex flex-shrink-0 items-baseline gap-1.5 font-serif">
                        <template v-if="promoApplies(child)">
                          <span class="inline-flex items-center rounded-sm bg-red-600 px-1 py-0.5 font-sans text-[9px] font-bold uppercase tracking-wider text-white">
                            {{ promotionProp.discount_percent_label }}
                          </span>
                          <span class="text-xs text-slate-400 line-through">${{ Number(child.price).toFixed(2) }}</span>
                          <span class="text-sm font-semibold tabular-nums text-slate-900">${{ Number(effectivePrice(child)).toFixed(2) }}</span>
                        </template>
                        <template v-else>
                          <span class="text-sm font-semibold tabular-nums text-slate-900">${{ child.price }}</span>
                        </template>
                      </span>
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <!-- Totals — clean, right-aligned. Total gets one hairline + serif size jump. -->
            <dl class="mx-6 mt-4 space-y-1.5 border-t border-slate-200 pt-4">
              <div class="flex justify-between text-sm">
                <dt class="text-slate-600">Subtotal</dt>
                <dd class="font-serif tabular-nums text-slate-900">$ {{ total.toFixed(2) }}</dd>
              </div>
              <div class="flex justify-between text-sm">
                <dt class="text-slate-600">Shipping</dt>
                <dd class="font-serif tabular-nums text-slate-900">$0.00</dd>
              </div>
              <div class="flex justify-between text-sm">
                <dt class="text-slate-600">Taxes</dt>
                <dd class="font-serif tabular-nums text-slate-900">$0.00</dd>
              </div>
              <div class="mt-2 flex items-baseline justify-between border-t border-slate-200 pt-3">
                <dt class="font-serif text-base text-slate-900">Total</dt>
                <dd class="font-serif text-2xl font-semibold tabular-nums text-slate-900">$ {{ total.toFixed(2) }}</dd>
              </div>
            </dl>

            <!-- Attestation — inline checkbox, no eyebrow pin, no card-within-card -->
            <div class="mx-6 mt-5">
              <label for="checkbox-1"
                     class="flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                     :class="tosCheckboxHasError ? 'border-red-500 bg-red-50' : 'border-slate-200'">
                <input
                    @blur="removeErrorsOnUpdate"
                    @change="handleTosClick" v-model="tos" id="checkbox-1" type="checkbox"
                    class="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-400 text-slate-900 focus:ring-2 focus:ring-slate-700">
                <span class="text-xs leading-relaxed text-slate-700">
                  I agree to the
                  <a
                      v-if="props.brandInfoProp?.tos_url"
                      :href="props.brandInfoProp.tos_url"
                      target="_blank"
                      rel="noopener"
                      class="font-medium text-slate-900 underline underline-offset-2">
                    Terms and Conditions
                  </a>
                  <span v-else class="font-medium">Terms and Conditions</span>
                  and refund policy.
                  <template v-if="addEinSection">
                    I also confirm I have signed Form SS-4 and Form 8821 above as the responsible party and reviewed them for accuracy.
                  </template>
                </span>
              </label>
              <ul v-if="tosCheckboxMessages.length" class="mt-1.5 ml-1 space-y-0.5">
                <li class="text-xs text-red-600" v-for="(error, index) in tosCheckboxMessages" :key="index">{{ error }}</li>
              </ul>
            </div>

            <!-- Submit + trust footer -->
            <div class="px-6 pb-6 pt-4">
              <button type="button"
                      @click="submit"
                      :disabled="loading"
                      class="group flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-5 py-3.5 text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60">
                <svg v-if="loading" aria-hidden="true"
                     class="h-4 w-4 animate-spin fill-white text-white/40"
                     viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                </svg>
                <span class="font-medium tracking-wide">
                  <span v-if="!loading">Submit Order</span>
                  <span v-else>Submitting…</span>
                </span>
                <svg v-if="!loading" xmlns="http://www.w3.org/2000/svg"
                     class="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                     viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M3 10a.75.75 0 0 1 .75-.75h10.638L10.23 5.29a.75.75 0 1 1 1.04-1.08l5.5 5.25a.75.75 0 0 1 0 1.08l-5.5 5.25a.75.75 0 1 1-1.04-1.08l4.158-3.96H3.75A.75.75 0 0 1 3 10Z" clip-rule="evenodd"/>
                </svg>
              </button>

              <!-- Trust footer — one short line -->
              <div class="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
                <LockClosedIcon class="h-3 w-3" aria-hidden="true"/>
                <span>Secure checkout · Processed by Stripe</span>
              </div>

              <!-- Error banner -->
              <div v-if="errorsPresent" class="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-700">
                <span v-if="submitErrorMessage">{{ submitErrorMessage }}</span>
                <span v-else>There are some errors above. Review the highlighted fields and try again.</span>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div v-else>
      <div class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">

            <div
                class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                       stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <h3 class="text-base font-semibold leading-6 text-gray-900" id="modal-title">Order Entered
                    Successfully</h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">The order has been created successfully. </p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-6">
                <a :href="route('admin.orders.index')">
                  <button type="button"
                          class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                    Go back to dashboard
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>



import FormBoirSection from '@/partials/Products/FormBoirSection.vue'
import FormEinSection from '@/partials/Products/FormEinSection.vue'
import {entityTypeForClassification} from '@/support/einEntityType'

import {defineEmits, onMounted, onBeforeUnmount, watch, reactive, toRaw, ref,  computed} from "vue";
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import {TrashIcon, PlusCircleIcon, LockClosedIcon} from "@heroicons/vue/20/solid";
import moment from 'moment';
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics';
import { usePageViewTracking } from '@/composables/usePageViewTracking';
import { useExperiment } from '@/composables/useExperiment';

const props = defineProps({
  documentProp: {
    type: Object,
    required: false,
    default: null,
  },
  jurisdictionProp: {
    type: Object,
    default: null,
  },
  productProp: {
    type: Object,
    required: false,
    default: null,
  },
  selectedItemsProp: {
    type: Array,
    required: false,
    default: null,
  },
  availableItemsProp: {
    type: Array,
    required: false,
    default: null,
  },
  companyNameProp: {
    type: String,
    required: false,
    default: null,
  },
  brandInfoProp: {
    type: Object,
    required: false,
    default: null,
  },
  promotionProp: {
    type: Object,
    required: false,
    default: null,
  },
  // Annual Report enrollment bundle: the generic annual_report service
  // product plus the per-state filing fee. Both are deferred yearly
  // subscriptions first billed on Jan 3 (OrderHelper forces the trial
  // anchor), so nothing is added to selectedItems or today's total.
  annualReportUpsellProp: {
    type: Object,
    required: false,
    default: null,
  },
  annualReportStateFeeProp: {
    type: Object,
    required: false,
    default: null,
  },


})


const document = ref(props.documentProp);

// The owner relation is always serialized (Document::$with) but can come back
// null when the owner row is soft-deleted (suppression/opt-out) — and a mailing
// record may carry no address at all. Every prefill reads through these so a
// missing owner leaves the field blank instead of throwing while the form is
// being constructed, which would blank the whole page.
const documentOwner = computed(() => document.value?.owner ?? null);
const documentOwnerAddress = computed(() => documentOwner.value?.addresses?.[0] ?? null);

const jurisdiction = ref(null);
const product = ref(props.productProp);
const documentItem = ref(null);
const children = ref([]);
const finished = ref(false);
const addEinSection = ref(false);
const addBoirSection = ref(false);
const addNewCompanySection = ref(true);
const companyName = ref(props.companyNameProp);
const tos = ref(null);
const loading = ref(false);
const tosText = ref(null);

const selectedItems = ref(props.selectedItemsProp);

// Late-mailer landing flow: the cart's primary product is an annual report
// (annual_report_late etc.), which turns on the past-due deadline banner.
const isAnnualReportOrder = computed(() =>
  (selectedItems.value || []).some(
    (i) => i && typeof i.code === 'string' && i.code.startsWith('annual_report')
  )
);

const annualReportEnrollment = computed(() => props.annualReportUpsellProp || null);
const annualReportEnrollmentStateFee = computed(() => props.annualReportStateFeeProp || null);

// Offered only when there's a real company to file for AND the jurisdiction
// has a state-fee product (the bundle is service + state fee, and the Jan 3
// anchor tracks that state's filing deadline — currently FL only). Never
// shown on late-mailer landings whose cart already holds an annual report
// product — the $0-today enrollment would read as a confusing duplicate of
// the paid filing.
const showAnnualReportEnrollment = computed(() =>
  Boolean(document.value?.document_number)
  && Boolean(annualReportEnrollment.value)
  && Boolean(annualReportEnrollmentStateFee.value)
  && !isAnnualReportOrder.value
);

const annualReportEnrollmentTotal = computed(() => {
  const service = Number(annualReportEnrollment.value?.price) || 0;
  const stateFee = Number(annualReportEnrollmentStateFee.value?.price) || 0;
  return (service + stateFee).toFixed(2);
});

const jurisdictionDisplayName = computed(() =>
  props.jurisdictionProp?.name
  || document.value?.jurisdiction?.name
  || document.value?.company?.jurisdiction?.name
  || 'your state'
);
const availableItems = ref(props.availableItemsProp);
const errorsPresent = ref(false);
const submitErrorMessage = ref(null);
const einSectionRef = ref(null);

// Red treatment is purely a function of the live checkbox state: the user
// is "in error" only while the box is unchecked. The moment they tick it,
// the red border goes away — regardless of whether stale validation errors
// from a prior submit still linger in form.errors. The error-message
// list below the checkbox is independently gated on the actual error keys
// so it keeps showing the message until the user retries.
const tosCheckboxHasError = computed(() => {
  if (!form) return false;
  if (form.tos) return false;
  const tosErr = form.errors?.tos;
  const einErr = form.errors?.['ein.forms_reviewed_confirmed'];
  const has = (v) => Array.isArray(v) ? v.length > 0 : !!v;
  return has(tosErr) || has(einErr);
});

// One checkbox now represents two backend rules (`tos` and, when EIN is in
// scope, `ein.forms_reviewed_confirmed`). Both rules return identical copy,
// so concatenating their messages would duplicate the same line under the
// checkbox. Dedupe by message text before render.
const tosCheckboxMessages = computed(() => {
  if (!form || form.tos) return [];
  const buckets = [
    form.errors?.tos,
    form.errors?.['ein.forms_reviewed_confirmed'],
  ];
  const flat = buckets
      .flatMap((b) => (Array.isArray(b) ? b : (b ? [b] : [])))
      .filter((m) => typeof m === 'string' && m.length > 0);
  return Array.from(new Set(flat));
});

watch(selectedItems, () => {
  checkForExtraFormFields();

}, {deep: true});


const laborLawPosterIndex = computed(() => {
  if (!availableItems.value) return -1;
  return availableItems.value.findIndex(
    (i) => i && typeof i.code === 'string' && i.code.startsWith('labor_law_poster')
  );
});

const laborLawPosterAvailable = computed(() => {
  const idx = laborLawPosterIndex.value;
  return idx >= 0 ? availableItems.value[idx] : null;
});

const isLaborLawPosterInCart = computed(() => {
  if (!selectedItems.value) return false;
  return selectedItems.value.some(
    (i) => i && typeof i.code === 'string' && i.code.startsWith('labor_law_poster')
  );
});

const addLaborLawPoster = () => {
  if (laborLawPosterIndex.value < 0) return;
  if (isLaborLawPosterInCart.value) return;
  addItems(laborLawPosterIndex.value);
};

const removeLaborLawPoster = () => {
  if (!selectedItems.value) return;
  for (const item of selectedItems.value) {
    if (item && typeof item.code === 'string' && item.code.startsWith('labor_law_poster')) {
      deleteItem(item.code);
      return;
    }
  }
};

// LLP placement A/B test. Picked here (in OrderSection setup) so every parent
// that mounts this component participates without extra wiring. Sticky per
// session via sessionStorage; mirrors into the URL `experiments` param so the
// existing page-view tracker and form submit forward it to the backend.
//   inline  (75%) — LLP appears in "Other Available Services" with strike-through pricing
//   callout (25%) — original amber upsell card above the list
const { variant: llpPlacementVariant } = useExperiment(
  'order_section_llp_placement',
  { inline: 3, callout: 1 }
);

const showLlpCallout = computed(() => llpPlacementVariant !== 'inline');

const isLaborLawPosterCode = (code) =>
  typeof code === 'string' && code.startsWith('labor_law_poster');

// In the 'inline' variant the LLP item is rendered inside the "Other Available
// Services" list (no amber callout). In the 'callout' variant the amber card
// owns LLP, so we still hide it from the list to avoid double-rendering.
const hideLlpFromOtherServices = (child) => {
  if (!child || !isLaborLawPosterCode(child.code)) return false;
  return showLlpCallout.value;
};

const promoApplies = (item) => {
  if (!props.promotionProp) return false;
  const codes = props.promotionProp.applicable_product_codes;
  if (!codes || codes.length === 0) return true;
  if (!item || !item.code) return false;
  return codes.some((p) => {
    if (typeof p !== 'string') return false;
    if (p.endsWith('*')) return item.code.startsWith(p.slice(0, -1));
    return p === item.code;
  });
};

const effectivePrice = (item) => {
  const original = Number(item?.price ?? 0);
  if (!promoApplies(item)) return original;
  const promo = props.promotionProp;
  if (promo.discount_type === 'percent') {
    return Math.round(original * (1 - Number(promo.discount_value) / 100) * 100) / 100;
  }
  return Math.max(0, Math.round((original - Number(promo.discount_value)) * 100) / 100);
};

const total = computed(() => {
  let calculatedTotal = 0;

  if (selectedItems.value) {


    for (let i = 0; i < selectedItems.value.length; i++) {
      calculatedTotal = Number(calculatedTotal) + Number(effectivePrice(selectedItems.value[i]));

    }
  }
  return calculatedTotal;

});


const hasPrimaryBoir = computed(() => {
  return hasPrimaryItem('boir');

});

const hasPrimaryEin = computed(() => {
  return hasPrimaryItem('ein_registration');

});


const form = reactive({
// const form = useForm('post', route('public.api.submit.order'), {
  first_name: documentOwner.value?.first_name ?? null,
  last_name: documentOwner.value?.last_name ?? null,
  email: documentOwner.value?.email ?? null,
  document_uuid: document.value?.uuid ?? null,
  address: documentOwnerAddress.value?.address ?? null,
  city: documentOwnerAddress.value?.city ?? null,
  state: documentOwnerAddress.value?.state || document.value?.company?.jurisdiction?.abr || props.jurisdictionProp?.abr || null,
  zip: documentOwnerAddress.value?.zip ?? null,
  country: documentOwnerAddress.value?.country ?? null,
  phone: documentOwner.value?.phone ?? null,
  company_name: document.value?.company?.name ?? null,
  company_address: document.value?.company?.addresses?.[0] ?? null,
  selectedItems: selectedItems.value,
  add_boir_section: false,
  add_ein_section: false,
  add_annual_report_upsell: false,
  annual_report_upsell_uuid: null,
  annual_report_state_fee_uuid: null,
  promo_code: props.promotionProp?.code ?? null,
  tos: null,
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_content: null,
  session_id: null,
  experiments: null,
  boir: {
    tax_identification_type: null,
    tax_identification_id: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    company_name: null,
    company_dba: null,
    receive_fincen_id: false,
    applicants: [],
    beneficiaries: [],
    company_address: {address: null, city: null, state: null, zip: null},
  },
  ein: {
    ein_first_name: null,
    ein_last_name: null,
    ein_county: null,
    ein_ssn: null,
    ein_member_number: null,
    ein_merchandise: null,
    ein_date_start: null,
    ein_entity_type: null,
    ein_before: 0,
    ein_before_specified: null,
    ein_reason: null,
    ein_activity: null,
    ein_further_info: null,
    ein_entity_type_specified: null,
    ein_entity_type_corporation_specified: null,
    question: {heavy_vehicle: null, gambling: null, w2: null, guns: null, quarterly: null},
    signature_fss4: null,
    signature_f8821: null,
    forms_reviewed_confirmed: false,
    forms_temp_token: null,
  },
  errors: {
    email: null,
    first_name: null,
    last_name: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    country: null,
    phone: null,
    tos: null,
    company_name: null,
    'ein.ein_first_name': null,
    'ein.ein_last_name': null,
    'ein.ein_county': null,
    'ein.ein_ssn': null,
    'ein.ein_member_number': null,
    'ein.ein_merchandise': null,
    'ein.ein_date_start': null,
    'ein.ein_entity_type': null,
    'ein.ein_before': 0,
    'ein.ein_before_specified': null,
    'ein.ein_reason': null,
    'ein.ein_activity': null,
    'ein.ein_further_info': null,
    'ein.ein_entity_type_specified': null,
    'ein.ein_entity_type_corporation_specified': null,
    'ein.question': {heavy_vehicle: null, gambling: null, w2: null, guns: null, quarterly: null},
    'ein.signature_fss4': null,
    'ein.signature_f8821': null,
    'ein.forms_reviewed_confirmed': null,

    'boir.tax_identification_type': null,
    'boir.tax_identification_id': null,
    'boir.address': null,
    'boir.city': null,
    'boir.state': null,
    'boir.zip': null,
    'boir.company_name': null,
    'boir.company_dba': null,
    'boir.receive_fincen_id': false,
    'boir.applicants': [{
      last_name: null,
      isMinor: null,
      first_name: null,
      dob: null,
      address: null,
      city: null,
      country: null,
      id_jurisdiction_country: null,
      id_jurisdiction_state: null,
      state: null,
      zip: null,
      document_type: null,
      document_id: null,
      document_region: null,
      document_image: null,

    }],
    'boir.beneficiaries': [
      {
        last_name: null,
        isMinor: null,
        first_name: null,
        dob: null,
        address: null,
        city: null,
        country: null,
        id_jurisdiction_country: null,
        id_jurisdiction_state: null,
        state: null,
        zip: null,
        document_type: null,
        document_id: null,
        document_region: null,
        document_image: null,

      }
    ],
    'boir.company_address': {address: null, city: null, state: null, zip: null},

  },

});

watch(annualReportEnrollment, (enrollment) => {
  if (!enrollment) {
    form.add_annual_report_upsell = false;
    form.annual_report_upsell_uuid = null;
  } else if (form.annual_report_upsell_uuid !== enrollment.uuid) {
    form.annual_report_upsell_uuid = enrollment.uuid;
  }
}, { immediate: true });

watch(annualReportEnrollmentStateFee, (stateFee) => {
  form.annual_report_state_fee_uuid = stateFee?.uuid ?? null;
}, { immediate: true });

function resetErrors() {

  form.errors = {
    email: null,
    first_name: null,
    last_name: null,
    address: null,
    city: null,
    state: null,
    zip: null,
    country: null,
    phone: null,
    tos: null,
    ein_further_info: null,
    company_name: null,
    'ein.ein_first_name': null,
    'ein.ein_last_name': null,
    'ein.ein_county': null,
    'ein.ein_ssn': null,
    'ein.ein_member_number': null,
    'ein.ein_merchandise': null,
    'ein.ein_date_start': null,
    'ein.ein_entity_type': null,
    'ein.ein_before': 0,
    'ein.ein_before_specified': null,
    'ein.ein_reason': null,
    'ein.ein_activity': null,
    'ein.ein_entity_type_specified': null,
    'ein.ein_entity_type_corporation_specified': null,
    'ein.question': {heavy_vehicle: null, gambling: null, w2: null, guns: null, quarterly: null},
    'ein.signature_fss4': null,
    'ein.signature_f8821': null,
    'ein.forms_reviewed_confirmed': null,

    'boir.tax_identification_type': null,
    'boir.tax_identification_id': null,
    'boir.address': null,
    'boir.city': null,
    'boir.state': null,
    'boir.zip': null,
    'boir.company_name': null,
    'boir.company_dba': null,
    'boir.receive_fincen_id': false,
    'boir.applicants': [{
      last_name: null,
      isMinor: null,
      first_name: null,
      dob: null,
      address: null,
      city: null,
      country: null,
      id_jurisdiction_country: null,
      id_jurisdiction_state: null,
      state: null,
      zip: null,
      document_type: null,
      document_id: null,
      document_region: null,
      document_image: null,
    }],
    'boir.beneficiaries': [
      {
        last_name: null,
        isMinor: null,
        first_name: null,
        dob: null,
        address: null,
        city: null,
        country: null,
        id_jurisdiction_country: null,
        id_jurisdiction_state: null,
        state: null,
        zip: null,
        document_type: null,
        document_id: null,
        document_region: null,
        document_image: null,
      }
    ],
    'boir.company_address': {address: null, city: null, state: null, zip: null},

  };


}


function checkForExtraFormFields() {

  let hasEin = false;
  let hasBoir = false;

  for (let i = 0; i < selectedItems.value.length; i++) {
    if (selectedItems.value[i].code == 'ein_registration') {
      hasEin = true;
    }
    if (selectedItems.value[i].code == 'boir') {
      hasBoir = true;
    }
  }

  addEinSection.value = hasEin;
  form.add_ein_section = hasEin;
  addBoirSection.value = hasBoir;
  form.add_boir_section = hasBoir;

}

function addItems(index) {

  if (!isItemAlreadyInCart(index)) {
    selectedItems.value.push({
      id: availableItems.value[index].id,
      uuid: availableItems.value[index].uuid,
      title: availableItems.value[index].name,
      code: availableItems.value[index].code,
      href: '#',
      price: availableItems.value[index].price,
      priceId: availableItems.value[index].priceId,
      subTitle: companyName.value,
      description: availableItems.value[index].description,
      image: availableItems.value[index].image,
      imageAlt: availableItems.value[index].name,
      isPrimary: false,
      add_subscription: false,
      has_subscription_option_product_id: availableItems.value[index].has_subscription_option_product_id || null,
      subscription_option_product: availableItems.value[index].subscription_option_product || null,
    })
  }


}

function setInitialFormValues() {


  if (companyName.value) {
    form.company_name = companyName.value;
  }


  if (document.value) {
    form.ein.ein_first_name = documentOwner.value?.first_name ?? null;
    form.ein.ein_last_name = documentOwner.value?.last_name ?? null;
    form.ein.ein_member_number = 1;
    form.ein.ein_county = null;
    form.ein.ein_ssn = null;
    form.ein.ein_merchandise = null;
    form.ein.ein_date_start = moment(document.value.notice_date).format('MM/DD/YYYY');
    form.ein.ein_reason = "Started new business";
    form.ein.ein_activity = "Other";
    form.ein.ein_activity_specified = null;

    form.boir.company_name = form.company_name;
    form.boir.company_address.address = documentOwnerAddress.value?.address ?? null;
    form.boir.company_address.city = documentOwnerAddress.value?.city ?? null;
    form.boir.company_address.state = documentOwnerAddress.value?.state ?? null;
    form.boir.company_address.zip = documentOwnerAddress.value?.zip ?? null;


    form.ein.ein_entity_type = entityTypeForClassification(
        document.value.company?.type?.general_classification
    );


  } else {
    form.ein.ein_entity_type = 'LLC';
    form.ein.ein_reason = "Started new business";
    form.ein.ein_activity = "Other";
  }

  form.jurisdictionName = props.jurisdictionProp?.name;

}

function isItemAlreadyInCart(index) {

  for (let i = 0; i < selectedItems.value.length; i++) {

    if (selectedItems.value[i].code === availableItems.value[index].code) {
      return true;
    }
  }
  return false;

}


function hasItem(code) {

  for (let i = 0; i < availableItems.value.length; i++) {
    if (selectedItems.value[i] && selectedItems.value[i].code === code) {
      return true;
    }
  }
  return false;

}

function isSubscriptionOptionForSelected(child) {
  if (!selectedItems.value || !child) {
    return false;
  }
  const childId = Number(child.id);
  const childUuid = child.uuid;
  return selectedItems.value.some(item => {
    if (!item) return false;
    const optionId = item.has_subscription_option_product_id != null
        ? Number(item.has_subscription_option_product_id)
        : (item.subscription_option_product?.id != null ? Number(item.subscription_option_product.id) : null);
    if (optionId !== null && optionId === childId) return true;
    const optionUuid = item.subscription_option_product?.uuid;
    return !!(optionUuid && childUuid && optionUuid === childUuid);
  });
}

function hasPrimaryItem(code) {

  for (let i = 0; i < availableItems.value.length; i++) {
    if (selectedItems.value[i] && selectedItems.value[i].code === code && selectedItems.value[i].isPrimary) {
      return true;
    }
  }
  return false;

}


function removeEin() {

  for (let i = 0; i < selectedItems.value.length; i++) {
    if (!selectedItems.value.isPrimary && selectedItems.value[i].code == 'ein_registration') {
      addEinSection.value = false;
      form.add_ein_section = false;
      selectedItems.value.splice(i, 1);
    }

  }
  return false;
}

function removeBoir() {
  for (let i = 0; i < selectedItems.value.length; i++) {
    if (!selectedItems.value.isPrimary && selectedItems.value[i].code == 'boir') {
      addBoirSection.value = false;
      form.add_boir_section = false;
      selectedItems.value.splice(i, 1);
    }

  }
  return false;

}


function addEinToSelectedItems() {


  for (let i = 0; i < availableItems.value.length; i++) {
    if (!availableItems.value.isPrimary && availableItems.value[i].code == 'ein_registration') {
      addEinSection.value = true;
      form.add_ein_section = true;
      addItems(i);
    }

  }
  return false;

}
function deleteItem(code) {

  for (let i = 0; i < selectedItems.value.length; i++) {
    if (selectedItems.value[i].code === code) {
      selectedItems.value.splice(i, 1);
    }
  }
}


// async function getProducts() {
//
//
// }

const updateBOIRForm = (updatedForm) => {
  // Same reactive proxy on both sides — Object.assign is symbolic.
  // We do NOT call removeErrorsOnUpdate() here for the same reason as
  // updateEINForm: it would wipe per-field BOIR errors moments after the
  // 422 catch hydrates them.
  Object.assign(form, updatedForm);
}
const updateEINForm = (updatedForm) => {
  // `form` and `updatedForm` are the same reactive proxy (FormEinSection
  // assigns `const form = props.formData`), so the Object.assign is largely
  // a no-op — left here for symmetry with updateBOIRForm.
  Object.assign(form, updatedForm);

  // Intentionally NOT calling removeErrorsOnUpdate() here. The deep watcher
  // in FormEinSection fires on every form mutation — including when we
  // hydrate per-field errors after a failed Generate. Calling the blanket
  // "clear errors if the field has any value" sweeper from this callback
  // wipes errors immediately after they're set, which is why the email
  // (and other personal-info) errors never reached the template after the
  // Generate request returned 422.
  // removeErrorsOnUpdate is still wired to each input's @blur, which is
  // the intended user-driven trigger.
}
const removeErrorsOnUpdate = () => {

  if (Array.isArray(form.boir.applicants)) {
    for (let i = 0; i < form.boir.applicants.length; i++) {
      if (form.boir.applicants[i].document_image) {
        form.errors['boir.applicants.' + i + '.document_image'] = null;
      }
      if (form.boir.applicants[i].first_name) {
        form.errors['boir.applicants.' + i + '.first_name'] = null;
      }
      if (form.boir.applicants[i].last_name) {
        form.errors['boir.applicants.' + i + '.last_name'] = null;
      }
      if (form.boir.applicants[i].address) {
        form.errors['boir.applicants.' + i + '.address'] = null;
      }
      if (form.boir.applicants[i].city) {
        form.errors['boir.applicants.' + i + '.city'] = null;
      }
      if (form.boir.applicants[i].state) {
        form.errors['boir.applicants.' + i + '.state'] = null;
      }
      if (form.boir.applicants[i].zip) {
        form.errors['boir.applicants.' + i + '.zip'] = null;
      }
      if (form.boir.applicants[i].dob) {
        form.errors['boir.applicants.' + i + '.dob'] = null;
      }
      if (form.boir.applicants[i].identificationId) {
        form.errors['boir.applicants.' + i + '.identificationId'] = null;
      }
      if (form.boir.applicants[i].document_id) {
        form.errors['boir.applicants.' + i + '.document_id'] = null;
      }
      if (form.boir.applicants[i].id_jurisdiction_country) {
        form.errors['boir.applicants.' + i + '.id_jurisdiction_country'] = null;
      }
      if (form.boir.applicants[i].id_jurisdiction_state) {
        form.errors['boir.applicants.' + i + '.id_jurisdiction_state'] = null;
      }
    }
  }
  if (Array.isArray(form.boir.beneficiaries)) {
    for (let i = 0; i < form.boir.beneficiaries.length; i++) {
      if (form.boir.beneficiaries[i].document_image) {
        form.errors['boir.beneficiaries.' + i + '.document_image'] = null;
      }
      if (form.boir.beneficiaries[i].first_name) {
        form.errors['boir.beneficiaries.' + i + '.first_name'] = null;
      }
      if (form.boir.beneficiaries[i].last_name) {
        form.errors['boir.beneficiaries.' + i + '.last_name'] = null;
      }
      if (form.boir.beneficiaries[i].address) {
        form.errors['boir.beneficiaries.' + i + '.address'] = null;
      }
      if (form.boir.beneficiaries[i].city) {
        form.errors['boir.beneficiaries.' + i + '.city'] = null;
      }
      if (form.boir.beneficiaries[i].state) {
        form.errors['boir.beneficiaries.' + i + '.state'] = null;
      }
      if (form.boir.beneficiaries[i].zip) {
        form.errors['boir.beneficiaries.' + i + '.zip'] = null;
      }
      if (form.boir.beneficiaries[i].dob) {
        form.errors['boir.beneficiaries.' + i + '.dob'] = null;
      }
      if (form.boir.beneficiaries[i].identificationId) {
        form.errors['boir.beneficiaries.' + i + '.identificationId'] = null;
      }
      if (form.boir.beneficiaries[i].document_id) {
        form.errors['boir.beneficiaries.' + i + '.document_id'] = null;
      }
      if (form.boir.beneficiaries[i].id_jurisdiction_country) {
        form.errors['boir.beneficiaries.' + i + '.id_jurisdiction_country'] = null;
      }
      if (form.boir.beneficiaries[i].id_jurisdiction_state) {
        form.errors['boir.beneficiaries.' + i + '.id_jurisdiction_state'] = null;
      }
    }
  }

  // Order Page
  if (form.first_name) {
    form.errors.first_name = null;
  }
  if (form.last_name) {
    form.errors.last_name = null;
  }
  if (form.phone) {
    form.errors.phone = null;
  }
  if (form.email) {
    form.errors.email = null;
  }
  if (form.company_name) {
    form.errors.company_name = null;
  }
  if (form.address) {
    form.errors.address = null;
  }
  if (form.city) {
    form.errors.city = null;
  }
  if (form.state) {
    form.errors.state = null;
  }
  if (form.zip) {
    form.errors.zip = null;
  }
  if (form.tos) {
    form.errors.tos = null;
  }
  // EIN Page
  if (form.ein.ein_first_name) {
    form.errors['ein.ein_first_name'] = null;
  }
  if (form.ein.ein_last_name) {
    form.errors['ein.ein_last_name'] = null;
  }
  if (form.ein.ein_county) {
    form.errors['ein.ein_county'] = null;
  }
  if (form.ein.ein_ssn) {
    form.errors['ein.ein_ssn'] = null;
  }
  if (form.ein.ein_entity_type) {
    form.errors['ein.ein_entity_type'] = null;
  }
  if (form.ein.ein_entity_type_corporation_specified) {
    form.errors['ein.ein_entity_type_corporation_specified'] = null;
  }
  if (form.ein.ein_entity_type_specified) {
    form.errors['ein.ein_entity_type_specified'] = null;
  }
  if (form.ein.ein_reason) {
    form.errors['ein.ein_reason'] = null;
  }
  if (form.ein.ein_date_start) {
    form.errors['ein.ein_date_start'] = null;
  }
  if (form.ein.ein_activity) {
    form.errors['ein.ein_activity'] = null;
  }
  if (form.ein.ein_activity_specified) {
    form.errors['ein.ein_activity_specified'] = null;
  }
  if (form.ein.ein_merchandise) {
    form.errors['ein.ein_merchandise'] = null;
  }
  if (form.ein.ein_before) {
    form.errors['ein.ein_before'] = null;
  }
  if (form.ein.ein_before_specified) {
    form.errors['ein.ein_before_specified'] = null;
  }
  if (form.ein.ein_further_info) {
    form.errors['ein.ein_further_info'] = null;
  }
  if (form.ein.question.heavy_vehicle) {
    form.errors['ein.question.heavy_vehicle'] = null;
  }
  if (form.ein.question.gambling) {
    form.errors['ein.question.gambling'] = null;
  }
  if (form.ein.question.quarterly) {
    form.errors['ein.question.quarterly'] = null;
  }
  if (form.ein.question.guns) {
    form.errors['ein.question.guns'] = null;
  }
  if (form.ein.question.w2) {
    form.errors['ein.question.w2'] = null;
  }
  if (form.ein.signature_fss4) {
    form.errors['ein.signature_fss4'] = null;
  }
  if (form.ein.signature_f8821) {
    form.errors['ein.signature_f8821'] = null;
  }


}

const clearTos = () => {
  form.tos = null;
  removeErrorsOnUpdate();
}


const handleTosClick = () => {

  form.tos = !form.tos;
  // Consolidated attestation: when the EIN section is present, the same
  // checkbox covers reviewing/confirming the generated SS-4 / 8821 forms.
  if (addEinSection.value && form.ein) {
    form.ein.forms_reviewed_confirmed = !!form.tos;
  }
  removeErrorsOnUpdate();

}

const submit = async () => {
  errorsPresent.value = false;
  submitErrorMessage.value = null;
  loading.value = true;
  // form.errors = null;
  resetErrors();

  // Arm the EIN section's UI nudge (red border, "Click here →" arrow, and
  // the inline message under the signature pad) when signature/forms aren't
  // ready. We intentionally do NOT abort the submit here: the backend
  // (CreateOrderRequest) is the authoritative validation surface and must be
  // allowed to run so the per-field errors on phone/email/county/etc. come
  // back the way they always have.
  if (einSectionRef.value && typeof einSectionRef.value.validateForSubmit === 'function') {
    einSectionRef.value.validateForSubmit();
  }

  form.promo_code = props.promotionProp?.code ?? null;

  await axios.post(route('public.api.submit.order'), form)
      .then(response => {
        if (response.data.data.result == 'success') {

          // Fire add_payment_info at the moment we hand off to Stripe's hosted
          // checkout. Card entry happens off-site, so this redirect is the
          // closest on-site signal that the visitor is committing to pay — it
          // completes the GA4 funnel between begin_checkout and purchase.
          const { trackAddPaymentInfo } = useGoogleAnalytics();
          trackAddPaymentInfo('Stripe Checkout', selectedItems.value, total.value);

          window.location.href = response.data.data.url;
          return;
        }

        // Backend caught an exception (e.g. Stripe rejected the session) and
        // returned a 200 with result: 'error' instead of a thrown response.
        // Surface it so the user isn't left staring at a silent button.
        submitErrorMessage.value = response.data?.data?.message
            || 'We could not place your order. Please try again or contact support.';
        errorsPresent.value = true;

        Sentry.withScope((scope) => {
          scope.setTag('layer', 'order_submit');
          scope.setTag('surface', 'standard_landing');
          scope.setTag('promo_code', form.promo_code ?? 'none');
          scope.setExtra('backend_message', response.data?.data?.message ?? null);
          scope.setExtra('document_uuid', form.document_uuid ?? null);
          Sentry.captureMessage(
              'OrderSection: backend returned result=error',
              'error'
          );
        });
      })
      .catch(error => {
        // Handle error here
        Sentry.withScope((scope) => {
          scope.setTag('layer', 'order_submit');
          scope.setTag('surface', 'standard_landing');
          scope.setTag('promo_code', form.promo_code ?? 'none');
          scope.setExtra('http_status', error.response?.status ?? null);
          scope.setExtra('http_data', error.response?.data ?? null);
          scope.setExtra('document_uuid', form.document_uuid ?? null);
          Sentry.captureException(error);
        });

        if (error.response?.data?.errors !== undefined) {
          form.errors = {
            ...form.errors,
            ...error.response.data.errors
          };
          errorsPresent.value = true;

          // If the backend specifically flagged the missing EIN preview token,
          // ask the EIN section to re-arm its "Click here →" nudge and surface
          // the message under the signature pad — that's where the action is.
          const tokenErr = form.errors['ein.forms_temp_token'];
          if (tokenErr && einSectionRef.value?.armGenerateNudge) {
            const msg = Array.isArray(tokenErr) ? tokenErr[0] : tokenErr;
            einSectionRef.value.armGenerateNudge(msg);
            submitErrorMessage.value = msg;
          }
          return;
        }

        submitErrorMessage.value = error.response?.data?.message
            || 'We could not place your order. Please try again or contact support.';
        errorsPresent.value = true;
      });
  loading.value = false;

};




onMounted(() => {

  checkForExtraFormFields();
  setInitialFormValues();

  // Always pre-include the Labor Law Poster when it's offered for this
  // document — independent of whether ?promo= is in the URL. The amber
  // upsell card still lets the visitor opt out via the checkbox. The
  // promotion (if any) only changes the discount display.
  if (laborLawPosterAvailable.value && !isLaborLawPosterInCart.value) {
    //addLaborLawPoster();
  }

  // Capture campaign attribution from the URL (set by the parent page's
  // useExperiment + usePageViewTracking) so the order is linked to the
  // recipient's experiment buckets in order_campaign_experiments.
  const params = new URLSearchParams(window.location.search);
  form.utm_source = params.get('utm_source');
  form.utm_medium = params.get('utm_medium');
  form.utm_campaign = params.get('utm_campaign');
  form.utm_content = params.get('utm_content');
  form.experiments = params.get('experiments');
  const { getOrCreateSessionId } = usePageViewTracking();
  form.session_id = getOrCreateSessionId();

  // Track begin_checkout event
  const { trackBeginCheckout } = useGoogleAnalytics();
  if (selectedItems.value && selectedItems.value.length > 0) {
    trackBeginCheckout(selectedItems.value, total.value);
  }

});
</script>
